import { Check, Loader2, Sparkles } from "lucide-react";
import type { TemplateStudioType } from "@shared/schema";
import { Button } from "@/components/ui/button";
import type { SuggestionState } from "@/lib/template-studio-library";

function SuggestionResults({
  available,
  state,
  onSuggest,
  onConfirm,
  scope,
  entityId,
  disabled = false,
}: {
  available: TemplateStudioType[];
  state: SuggestionState | undefined;
  onSuggest: () => void;
  onConfirm: (typeId: string) => void;
  scope: "library" | "performer";
  entityId: string;
  disabled?: boolean;
}) {
  const matchingTypes = new Map(available.map((type) => [type.id, type]));
  const suggestTestId = scope === "library" ? `suggest-library-type-${entityId}` : `suggest-performer-type-${entityId}`;
  return <>
    {!state?.suggestions && <Button type="button" variant="outline" size="sm" disabled={disabled || state?.loading} onClick={onSuggest}
      aria-label={scope === "library" ? `Suggest a board type for reference ${entityId}` : undefined} data-testid={suggestTestId}>
      {state?.loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
      {state?.error ? "Retry suggestions" : "Suggest type"}
    </Button>}
    {state?.error && <div role="alert" className="space-y-2 text-xs text-destructive">
      <p>{state.error}</p>
      <Button type="button" variant="outline" size="sm" disabled={disabled || state.loading} onClick={onSuggest}>Retry</Button>
    </div>}
    {state?.suggestions && state.suggestions.length === 0 && <div role="status" className="space-y-2 text-xs text-muted-foreground">
      <p>No board type suggestions found.</p>
      <Button type="button" variant="outline" size="sm" disabled={disabled || state.loading} onClick={onSuggest}>Retry suggestions</Button>
    </div>}
    {!!state?.suggestions?.length && <ul className="space-y-2">
      {state.suggestions.map((suggestion) => {
        const type = matchingTypes.get(suggestion.typeId);
        if (!type) return null;
        const suggestionTestId = scope === "library"
          ? `type-suggestion-${entityId}-${type.id}`
          : `performer-type-suggestion-${entityId}-${type.id}`;
        const confirmTestId = scope === "library"
          ? `confirm-library-suggestion-${entityId}-${type.id}`
          : `confirm-performer-suggestion-${entityId}-${type.id}`;
        return <li key={suggestion.typeId} className="flex flex-wrap items-center justify-between gap-2 text-xs" data-testid={suggestionTestId}>
          <span><strong>{type.name}</strong> · {suggestion.reason} · {suggestion.confidence} confidence</span>
          <Button type="button" size="sm" variant="secondary" disabled={disabled} onClick={() => onConfirm(type.id)}
            aria-label={scope === "library" ? `Assign ${type.name} to reference` : `Confirm ${type.name} for performer`}
            data-testid={confirmTestId}>
            {scope === "library" && <Check className="mr-1 h-3.5 w-3.5" />}Confirm
          </Button>
        </li>;
      })}
    </ul>}
  </>;
}

export function LibraryTypeSuggestions({
  imageId,
  available,
  state,
  onSuggest,
  onConfirm,
  disabled,
}: {
  imageId: string;
  available: TemplateStudioType[];
  state: SuggestionState | undefined;
  onSuggest: () => void;
  onConfirm: (typeId: string) => void;
  disabled?: boolean;
}) {
  return <div className="space-y-2 rounded-md border border-dashed border-violet-200 bg-white p-2.5" data-testid={`library-suggestion-${imageId}`}>
    <p className="text-[11px] leading-relaxed text-slate-600">Suggest type uses credits. It runs only when you activate the button below; nothing is assigned unless you confirm.</p>
    <SuggestionResults available={available} state={state} onSuggest={onSuggest} onConfirm={onConfirm} scope="library" entityId={imageId} disabled={disabled} />
  </div>;
}

export function PerformerTypeSuggestions({
  adId,
  available,
  state,
  onSuggest,
  onConfirm,
}: {
  adId: string;
  available: TemplateStudioType[];
  state: SuggestionState | undefined;
  onSuggest: () => void;
  onConfirm: (typeId: string) => void;
}) {
  return <div className="space-y-2 rounded-md border border-dashed border-violet-200 bg-white p-2.5" data-testid={`performer-suggestion-${adId}`}>
    <p className="text-[11px] leading-relaxed text-slate-600">Suggest type uses credits. It runs only when you activate the button below; nothing is assigned unless you confirm.</p>
    <SuggestionResults available={available} state={state} onSuggest={onSuggest} onConfirm={onConfirm} scope="performer" entityId={adId} />
  </div>;
}